import React, { useState, useCallback } from 'react';
import { Form, Input, Button, message } from 'antd';
import { auth } from '../../services/api';
import styled from 'styled-components';

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

interface RegisterFormProps {
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({
  onSuccess,
  onSwitchToLogin
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // 处理倒计时
  React.useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleResend = useCallback(async () => {
    try {
      const values = form.getFieldsValue();
      if (!values.email || !values.password) {
        message.error('请填写邮箱和密码');
        return;
      }

      setResendLoading(true);
      await auth.resendVerification(values.email, values.password);
      message.success('验证邮件已重新发送，请查收');
      setCountdown(60); // 开始60秒倒计时
    } catch (error: any) {
      if (error.response?.status === 400) {
        message.error(error.response.data.detail || '邮箱或密码错误');
      } else {
        message.error(error.response?.data?.detail || '发送失败，请稍后重试');
      }
    } finally {
      setResendLoading(false);
    }
  }, [form]);

  const handleRegister = async (values: any) => {
    try {
      setLoading(true);
      const response = await auth.register({
        email: values.email,
        password: values.password
      });
      message.success(response.message || '注册成功，请查收验证邮件');
      setCountdown(60); // 注册成功后开始倒计时
      onSwitchToLogin?.();
    } catch (error: any) {
      if (error.response?.status === 400) {
        message.error('该邮箱已被注册');
      } else if (error.response?.data?.detail) {
        message.error(typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : '注册失败，请稍后重试');
      } else {
        message.error('注册失败，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleRegister}
      preserve={false}
      onFinishFailed={({ errorFields }) => {
        form.scrollToField(errorFields[0].name);
      }}
    >
      <Form.Item
        name="email"
        label="邮箱"
        rules={[
          { required: true, message: '请输入邮箱' },
          { type: 'email', message: '请输入有效的邮箱地址' }
        ]}
      >
        <Input placeholder="请输入邮箱" />
      </Form.Item>

      <Form.Item
        name="password"
        label="密码"
        rules={[
          { required: true, message: '请输入密码' },
          { min: 8, message: '密码长度至少为8位' }
        ]}
      >
        <Input.Password placeholder="请输入密码" />
      </Form.Item>

      <Form.Item
        name="confirmPassword"
        label="确认密码"
        dependencies={['password']}
        rules={[
          { required: true, message: '请确认密码' },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('两次输入的密码不一致'));
            },
          }),
        ]}
      >
        <Input.Password placeholder="请再次输入密码" />
      </Form.Item>

      <Form.Item>
        <ButtonGroup>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{ flex: 1 }}
          >
            注册
          </Button>
          <Button
            onClick={handleResend}
            loading={resendLoading}
            disabled={countdown > 0}
            style={{ minWidth: '140px' }}
          >
            {countdown > 0 ? `重新发送(${countdown}s)` : '重新发送激活邮件'}
          </Button>
        </ButtonGroup>
      </Form.Item>

      <div style={{ textAlign: 'center', marginTop: '16px' }}>
        <span style={{ color: '#8c8c8c' }}>已有账号？</span>
        {' '}
        <Button type="link" onClick={onSwitchToLogin} style={{ padding: '0 4px' }}>
          立即登录
        </Button>
      </div>
    </Form>
  );
};

export default RegisterForm; 